
import { LeadStatus } from '@/types/lead';

export interface StatusOption {
  value: LeadStatus;
  label: string;
  color: string;
  description?: string;
}

export const getLeadStatusOptions = (): StatusOption[] => {
  return [
    { 
      value: 'new', 
      label: 'New Lead', 
      color: 'bg-blue-500',
      description: 'Recently added to the system, not yet contacted'
    },
    { 
      value: 'contacted', 
      label: 'Contacted', 
      color: 'bg-yellow-500',
      description: 'Initial outreach has been made'
    },
    { 
      value: 'qualified', 
      label: 'Qualified', 
      color: 'bg-purple-500',
      description: 'Lead has been qualified as a potential opportunity'
    },
    { 
      value: 'negotiation', 
      label: 'Negotiating', 
      color: 'bg-orange-500',
      description: 'In active discussions or proposal stage'
    },
    { 
      value: 'proposal', 
      label: 'Proposal', 
      color: 'bg-indigo-500',
      description: 'Formal proposal has been submitted'
    },
    { 
      value: 'closed_won', 
      label: 'Won', 
      color: 'bg-green-500',
      description: 'Successfully closed deal'
    },
    { 
      value: 'closed_lost', 
      label: 'Lost', 
      color: 'bg-red-500',
      description: 'Opportunity has been lost'
    }
  ];
};

export const getStatusDisplayInfo = (status?: LeadStatus): StatusOption => {
  const defaultStatus: StatusOption = { 
    value: 'new', 
    label: 'New Lead', 
    color: 'bg-blue-500'
  };
  
  if (!status) return defaultStatus;
  
  const statusOption = getLeadStatusOptions().find(option => option.value === status);
  return statusOption || defaultStatus;
};

// Add a function to get the next steps based on lead status
export const getNextStepsForStatus = (status: LeadStatus): string => {
  switch (status) {
    case 'new':
      return 'Make initial contact through email or phone';
    case 'contacted':
      return 'Schedule a discovery call to determine needs and fit';
    case 'qualified':
      return 'Prepare and deliver a custom proposal';
    case 'proposal':
      return 'Follow up on the proposal and address any concerns';
    case 'negotiation':
      return 'Finalize terms and prepare closing documents';
    case 'closed_won':
      return 'Begin onboarding process and schedule kick-off meeting';
    case 'closed_lost':
      return 'Schedule follow-up in 3-6 months for potential re-engagement';
    default:
      return 'Determine next steps based on current situation';
  }
};

// Calculate lead success rate
export const calculateLeadSuccessRate = (leads: { status?: LeadStatus }[]): number => {
  if (!leads.length) return 0;
  
  const closedWonLeads = leads.filter(lead => lead.status === 'closed_won').length;
  return (closedWonLeads / leads.length) * 100;
};
